import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

/*
 * Monogram colour.
 *
 * Replaces the eight-colour flat-UI array, none of whose values were brand
 * colours. Five semantic families indexed by sum(charCodes) % 5 — deterministic
 * per name, so a user keeps the same colour on every screen. All five pairings
 * are >= 6.7:1 and legible at 24px.
 *
 * Spec: core-handover/04-icons-and-charts.md
 */
const FAMILIES = [
  'bg-success-100 text-success-700',
  'bg-warning-100 text-warning-700',
  'bg-danger-100 text-danger-700',
  'bg-info-100 text-info-700',
  'bg-brand-100 text-brand-700',
];

const family = (name) =>
  FAMILIES[
    [...(name || '')].reduce((sum, c) => sum + c.charCodeAt(0), 0) %
      FAMILIES.length
  ];

const parseInitials = (str) => {
  const arr = (str || '').trim().split(' ');
  if (!str) return '';
  if (arr.length < 2) return `${str[0] || ''}${str[1] || ''}`.toUpperCase();
  return `${arr[0][0]}${arr[1][0]}`.toUpperCase();
};

function ThumbIcon({
  url,
  icon: Icon,
  iconClassName,
  alt,
  className,
  imgClassName,
  initialsClassName,
}) {
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const tone = family(alt);
  React.useEffect(() => {
    if (!url) setImageLoaded(false);
  }, [url, setImageLoaded]);
  return (
    <div
      className={cn(
        'flex justify-center flex-none items-center text-center leading-6 relative overflow-hidden',
        !url && tone,
        className,
      )}
    >
      {!url ? (
        <span className={cn('text-badge', initialsClassName)}>
          {Icon ? <Icon className={iconClassName} /> : parseInitials(alt)}
        </span>
      ) : null}
      <img
        className={cn('absolute h-full w-full', imgClassName, {
          'opacity-0': !imageLoaded,
        })}
        src={url}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageLoaded(false)}
        alt={alt}
      />
    </div>
  );
}

ThumbIcon.defaultProps = {
  url: null,
  className: '',
  alt: '',
  icon: null,
  iconClassName: 'h-4 w-4',
  imgClassName: 'object-cover object-center',
  initialsClassName: '',
};

ThumbIcon.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.instanceOf(Object)]),
  iconClassName: PropTypes.string,
  imgClassName: PropTypes.string,
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Object)]),
  alt: PropTypes.string,
  className: PropTypes.string,
  initialsClassName: PropTypes.string,
};

export default React.memo(ThumbIcon);
